import { getSite } from '@/utils/index';
import axios from 'lib/data/axios';
import logger from 'lib/utils/logger';
import getConfig from 'modules/config';
import { useCallback, useEffect, useState } from 'react';
import { isNotTrue } from 'typesafe-utils';
import { useJWPlayer } from '../JWPlayerContext';
import { JW_MEDIA_URL, RECOMMENDATION_API_URL, VIEWED_VIDEOS } from '../constants';
import { Play } from '../constants/playEvents';
import { PlaylistItem } from '../types/video';
import { getVideoRecommendationsAuthToken } from '../utils/getVideoRecommendationsAuthToken';
import { usePlayerEvent } from './usePlayerEvent';

export function useNextRecommendedVideo(setShowNextVideoPopup: (show: boolean) => void) {
  const { player, currentMedia } = useJWPlayer();
  const [viewedVideos, setViewedVideos] = useState<string[]>();
  const [nextVideo, setNextVideo] = useState<PlaylistItem>();
  const [accessToken, setAccessToken] = useState<string>();
  const brand = getSite();
  const isDisabled = isNotTrue(getConfig('nextVideoRecommendation.enabled'));

  useEffect(() => {
    if (isDisabled) return;

    setAccessToken(getVideoRecommendationsAuthToken());

    // load initial value from sessionStorage
    if (!viewedVideos) {
      setViewedVideos(getViewedVideos());
    }

    if (viewedVideos?.length) {
      updateViewedVideos(viewedVideos);

      if (player && brand) {
        loadNextRecommendedVideo(viewedVideos, brand).then((media) => {
          media && setNextVideo(media);
        });
      }
    }
  }, [viewedVideos, brand, player]);

  useEffect(() => {
    if (!player || !nextVideo) return;

    setShowNextVideoPopup(false);

    player?.setPlaylistItemCallback(() => {
      if (!nextVideo) {
        return;
      }

      return new Promise((resolve) => {
        if (nextVideo) {
          resolve(nextVideo);
        }
      });
    });
  }, [nextVideo, player]);

  const onPlay = useCallback(async () => {
    if (!currentMedia || !viewedVideos) return;

    if (!viewedVideos.includes(currentMedia.mediaid)) {
      setViewedVideos([currentMedia.mediaid, ...viewedVideos]);
    }
  }, [currentMedia, viewedVideos]);

  usePlayerEvent(player, Play, onPlay);

  function getViewedVideos(): string[] {
    const viewedVideosJson: string | null = sessionStorage.getItem(VIEWED_VIDEOS);

    if (viewedVideosJson === null) {
      return [];
    }

    try {
      const viewedVideos: string[] = JSON.parse(viewedVideosJson);
      if (Array.isArray(viewedVideos)) {
        return viewedVideos;
      }
    } catch (error) {
      logger.catch('Error parsing viewed videos JSON:', error);
    }

    return [];
  }

  function updateViewedVideos(viewedVideos: string[]): void {
    sessionStorage.setItem(VIEWED_VIDEOS, JSON.stringify(viewedVideos));
  }

  async function loadNextRecommendedVideo(viewedVideos: string[], brand: string): Promise<PlaylistItem | undefined> {
    if (!accessToken) return;

    const nextRecommendedVideoId = await getNextRecommendedVideoId(viewedVideos, brand);

    if (!nextRecommendedVideoId) return;

    const response = await axios.get(`${JW_MEDIA_URL}/${nextRecommendedVideoId}`);

    if (response?.status === 200) {
      return response?.data?.playlist[0] ?? null;
    }
  }

  async function getNextRecommendedVideoId(viewedContent: string[], brand: string): Promise<string> {
    const payload = JSON.stringify({
      viewedContent,
      brand,
    });
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      timeout: 60000,
    };

    try {
      const response = await axios.post(RECOMMENDATION_API_URL, payload, config);

      return response?.data?.nextVideoRecommendation ?? '';
    } catch (error) {
      logger.catch(`Unable to get next recommended video ID: ${error}`);

      return '';
    }
  }
}
